import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { CircleCheck, RotateCcw, Truck } from "lucide-react";
import { Button } from "../../../components/shadcn/ui/button";
import { Notice } from "./FieldPrimitives";
import { patientName } from "../providerApi";
import {
  Ear,
  StockItem,
  StockVisit,
  VisitResident,
  VisitResidentsPage,
  deviceLine,
  providerStockApi,
  shortDay,
  unanswered,
  useActionKeys,
} from "./providerStockApi";
import "./field-stock.css";

/**
 * Device readiness, extended (Isaiah Cotto, 2026-10-22): "Load from my stock" for the ATD-approved residents of the
 * provider's own upcoming visit at this building, and "In my car" -- what is loaded for each upcoming visit.
 *
 * A load is a PLAN ONLY ("load for tomorrow"): it never records a dispense, never changes ATD and never creates a
 * commission; Scheduling's dispense stays separate. The server decides every load (the visit is yours and upcoming,
 * the resident is ATD-approved and yours to see, the device is yours and in stock); this panel only offers it.
 * A login with no provider record (the office, Scheduling) sees nothing here.
 */
export interface StockLoadPanelProps {
  facilityId: string;
  offline?: boolean;
}

const STALE = "Your stock changed since this list loaded (another tab or device). It was reloaded; choose again.";
const NOT_CONFIRMED = "eMerge did not confirm. Nothing is shown as loaded. Try again; it will not be loaded twice.";

type Phase = { state: "loading" } | { state: "hidden" } | { state: "ready" } | { state: "error"; message: string };

function ResidentRow({
  resident,
  planId,
  canLoad,
  stock,
  disabled,
  maxPerResident,
  onChanged,
}: {
  resident: VisitResident;
  planId: string;
  canLoad: boolean;
  stock: StockItem[];
  disabled: boolean;
  maxPerResident: number;
  onChanged: (message: string) => void;
}) {
  const ids = useId();
  const name = patientName(resident);
  const [itemId, setItemId] = useState("");
  const [ear, setEar] = useState<Ear | "">("");
  const { keyFor, settle } = useActionKeys();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const chosen = stock.find((s) => s.id === itemId) || null;
  const full = resident.loaded.length >= maxPerResident;
  const earsUsed = resident.loaded.map((l) => l.ear).filter(Boolean);

  const act = async (action: string, call: (k: string) => Promise<{ success: boolean; status?: number; code?: string; message?: string; error?: string }>, done: string) => {
    setBusy(true);
    setError("");
    try {
      const r = await call(keyFor(action));
      if (r.success) {
        settle(action);
        setItemId("");
        setEar("");
        onChanged(done);
      } else if (unanswered(r)) {
        setError(NOT_CONFIRMED);
      } else if (r.code === "STALE_VERSION" || r.code === "ITEM_ALREADY_LOADED") {
        settle(action);
        setItemId("");
        onChanged(r.code === "STALE_VERSION" ? STALE : r.message || STALE);
      } else {
        settle(action);
        setError(r.message || r.error || "Not loaded.");
      }
    } catch {
      setError(NOT_CONFIRMED);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="fw-stock-item">
      <div className="fw-stock-item-head">
        <strong>{name}</strong>
        <span className={`fw-stock-tag ${resident.loaded.length ? "assigned" : "in_stock"}`}>
          {resident.loaded.length ? <Truck size={14} aria-hidden="true" /> : <CircleCheck size={14} aria-hidden="true" />}
          {resident.loaded.length ? `${resident.loaded.length} loaded` : "ATD approved"}
        </span>
      </div>
      <p className="fw-muted">Room {resident.room_number || "not recorded"}</p>
      {resident.loaded.length > 0 && (
        <ul className="fw-stock-loaded" aria-label={`Loaded for ${name}`}>
          {resident.loaded.map((l) => (
            <li key={l.load_id}>
              <span>{deviceLine(l)}</span>
              {canLoad && (
                <Button
                  variant="outline"
                  type="button"
                  disabled={disabled || busy}
                  onClick={() => act(`release:${l.load_id}`, (k) => providerStockApi.release(l.item_id, l.item_version, k), "Released back to your stock.")}
                  aria-label={`Release ${deviceLine(l)} for ${name} back to stock`}
                >
                  <RotateCcw size={14} aria-hidden="true" /> Release
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canLoad && !full && (
        <div className="fw-stock-load" role="group" aria-label={`Load from my stock for ${name}`}>
          <label htmlFor={`${ids}-item`}>Device from my stock</label>
          <select id={`${ids}-item`} value={itemId} onChange={(e) => setItemId(e.target.value)} disabled={disabled || busy || stock.length === 0}>
            <option value="">{stock.length ? "Choose a hearing aid" : "No hearing aid in stock"}</option>
            {stock.map((s) => (
              <option key={s.id} value={s.id}>{deviceLine(s)}</option>
            ))}
          </select>
          {chosen && !chosen.ear && (
            <>
              <label htmlFor={`${ids}-ear`}>Ear (optional)</label>
              <select id={`${ids}-ear`} value={ear} onChange={(e) => setEar(e.target.value as Ear | "")} disabled={busy}>
                <option value="">Not set</option>
                <option value="LEFT" disabled={earsUsed.includes("LEFT")}>Left</option>
                <option value="RIGHT" disabled={earsUsed.includes("RIGHT")}>Right</option>
              </select>
            </>
          )}
          {error && <p role="alert" className="fw-error">{error}</p>}
          <Button
            className="fw-primary"
            type="button"
            disabled={disabled || busy || !chosen}
            onClick={() => chosen && act(`load:${chosen.id}:${ear}`, (k) => providerStockApi.load(planId, {
              item_id: chosen.id, patient_id: resident.patient_id, version: chosen.version, ...(ear ? { ear } : {}),
            }, k), `Loaded for ${name}.`)}
          >
            {busy ? "Loading…" : "Load from my stock"}
          </Button>
        </div>
      )}
      {canLoad && full && <p className="fw-muted">Two devices are loaded for this resident on this visit.</p>}
    </li>
  );
}

export default function StockLoadPanel({ facilityId, offline = false }: StockLoadPanelProps) {
  const ids = useId();
  const [phase, setPhase] = useState<Phase>({ state: "loading" });
  const [visits, setVisits] = useState<StockVisit[]>([]);
  const [planId, setPlanId] = useState("");
  const [stock, setStock] = useState<StockItem[]>([]);
  const [residents, setResidents] = useState<VisitResidentsPage | null>(null);
  const [residentsError, setResidentsError] = useState("");
  const [status, setStatus] = useState("");

  const refresh = useCallback(async (keepPlan?: string) => {
    try {
      const [v, s] = await Promise.all([providerStockApi.visits(facilityId), providerStockApi.list()]);
      if (v.success && v.data && !v.data.linked) { setPhase({ state: "hidden" }); return; }
      if (!v.success || !v.data || !Array.isArray(v.data.visits)) {
        // A login that may not see visit plans, or a server without the migration: say nothing in this panel.
        if (v.status === 403 || v.code === "PROVIDER_STOCK_NOT_INSTALLED") { setPhase({ state: "hidden" }); return; }
        setPhase({ state: "error", message: unanswered(v) ? "Could not reach eMerge to load your visits." : v.message || "Your visits could not be loaded." });
        return;
      }
      setVisits(v.data.visits);
      setStock(s.success && s.data && Array.isArray(s.data.items) ? s.data.items.filter((i) => i.state === "IN_STOCK" && i.kind === "HEARING_AID") : []);
      const wanted = keepPlan && v.data.visits.some((x) => x.plan_id === keepPlan) ? keepPlan : v.data.visits[0]?.plan_id || "";
      setPlanId(wanted);
      setPhase({ state: "ready" });
    } catch {
      setPhase({ state: "error", message: "Could not reach eMerge to load your visits." });
    }
  }, [facilityId]);

  const loadResidents = useCallback(async (id: string) => {
    setResidentsError("");
    if (!id) { setResidents(null); return; }
    try {
      const r = await providerStockApi.residents(id);
      if (r.success && r.data && Array.isArray(r.data.residents)) setResidents(r.data);
      else {
        setResidents(null);
        setResidentsError(unanswered(r) ? "Could not reach eMerge to load this visit's residents." : r.message || "This visit's residents could not be loaded.");
      }
    } catch {
      setResidents(null);
      setResidentsError("Could not reach eMerge to load this visit's residents.");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (phase.state === "ready") loadResidents(planId); }, [planId, phase.state, loadResidents]);

  const changed = async (message: string) => {
    setStatus(message);
    await refresh(planId);
    await loadResidents(planId);
  };

  const selected = useMemo(() => visits.find((v) => v.plan_id === planId) || null, [visits, planId]);
  if (phase.state === "hidden") return null;

  return (
    <section className="fw-panel-content fw-stock" aria-labelledby={`${ids}-title`}>
      <h3 id={`${ids}-title`} className="fw-stock-subtitle">
        <Truck size={16} aria-hidden="true" /> Load from my stock
      </h3>
      <p className="fw-muted">
        Plan which devices go in the car for a visit. Loading is a plan only: it does not record a dispense, change ATD
        or create a commission.
      </p>
      {phase.state === "loading" && <p role="status" className="fw-muted">Loading your visits…</p>}
      {phase.state === "error" && (
        <div role="status" className="fw-error">
          <p>{phase.message}</p>
          <Button variant="outline" type="button" onClick={() => refresh()}>Try again</Button>
        </div>
      )}
      {phase.state === "ready" && visits.length === 0 && (
        <p className="fw-muted">No upcoming visit of yours at this building. Loading opens when Scheduling plans one.</p>
      )}
      {phase.state === "ready" && visits.length > 0 && (
        <>
          <p role="status" className="fw-save-status">{status}</p>
          <div className="fw-stock-car" role="group" aria-label="In my car">
            <h4>In my car</h4>
            <ul>
              {visits.map((v) => (
                <li key={v.plan_id}>
                  <strong>{shortDay(v.date)}</strong> · {v.facility.name || "This building"}: {v.loaded.length} device{v.loaded.length === 1 ? "" : "s"} loaded
                  {v.loaded.length > 0 && (
                    <ul>
                      {v.loaded.map((l) => <li key={l.load_id}>{deviceLine(l)}</li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {visits.length > 1 && (
            <>
              <label htmlFor={`${ids}-visit`}>Visit</label>
              <select id={`${ids}-visit`} value={planId} onChange={(e) => setPlanId(e.target.value)}>
                {visits.map((v) => (
                  <option key={v.plan_id} value={v.plan_id}>{shortDay(v.date)}{v.arrival_time ? ` at ${v.arrival_time}` : ""}</option>
                ))}
              </select>
            </>
          )}
          {selected && visits.length === 1 && <p className="fw-muted">Visit: {shortDay(selected.date)}</p>}
          {offline && <Notice>Offline: loading is unavailable until you reconnect.</Notice>}
          {residentsError && <p role="alert" className="fw-error">{residentsError}</p>}
          {residents && (
            <>
              {!residents.can_load && <p className="fw-muted">This visit can be read but not loaded (it is no longer upcoming, or your access here is read-only).</p>}
              {residents.residents.length === 0 ? (
                <p className="fw-muted">No ATD-approved resident of yours at this building.</p>
              ) : (
                <ul className="fw-stock-list" aria-label="ATD-approved residents for this visit">
                  {residents.residents.map((r) => (
                    <ResidentRow
                      key={r.patient_id}
                      resident={r}
                      planId={residents.visit.plan_id}
                      canLoad={residents.can_load}
                      stock={stock}
                      disabled={offline}
                      maxPerResident={residents.max_per_resident || 2}
                      onChanged={changed}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
